<template>
  <div>
    <h2 class="mb-5">Molar Mass Determination - Data & Calculations</h2>

    <p class="mb-3">a) What is the sample number from the stock bottle you used?</p>

    <calculation-input
      v-model="inputs.sampleNumber"
      class="mb-5"
      prepend-text="$\text{Sample Number}:$"
      :disabled="!allowEditing"
    />

    <p class="mb-3">b) Fill in the table below with the results/calculations of your experiment.</p>

    <p class="pl-n8 mb-5">
      <v-simple-table>
        <thead>
          <tr>
            <td style="font-weight: bold; text-align: left">Property</td>
            <td style="font-weight: bold; text-align: center">Trial 1</td>
            <td style="font-weight: bold; text-align: center">Trial 2</td>
            <td style="font-weight: bold; text-align: center">Trial 3</td>
            <td style="font-weight: bold; text-align: left">Units</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>i) Mass (flask + 2.5" x 2.5" Al foil + rubber band)</td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.emptyMass"
            >
              <calculation-input
                v-model="inputs[answer.emptyMass]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{g}$" /></td>
          </tr>

          <tr>
            <td>ii) Mass (of above + compound after heating)</td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.fullMass"
            >
              <calculation-input
                v-model="inputs[answer.fullMass]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{g}$" /></td>
          </tr>

          <tr>
            <td>iii) Mass (condensed vapour), <stemble-latex content="$m$" /></td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.massVapour"
            >
              <calculation-input
                v-model="inputs[answer.massVapour]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{g}$" /></td>
          </tr>

          <tr>
            <td colspan="5" style="font-weight: bold">Vapour Data</td>
          </tr>

          <tr>
            <td>iv) Volume, <stemble-latex content="$V$" /></td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.volume"
            >
              <calculation-input
                v-model="inputs[answer.volume]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{mL}$" /></td>
          </tr>

          <tr>
            <td>v) Temperature, <stemble-latex content="$T$" /></td>
            <td v-for="answer in answers" class="py-2 px-2 mx-0 centered-input" :key="answer.temp">
              <calculation-input
                v-model="inputs[answer.temp]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{K}$" /></td>
          </tr>

          <tr>
            <td>vi) Pressure, <stemble-latex content="$P$" /></td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.pressure"
            >
              <calculation-input
                v-model="inputs[answer.pressure]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{kPa}$" /></td>
          </tr>

          <tr>
            <td colspan="5" style="font-weight: bold">&nbsp;</td>
          </tr>

          <tr>
            <td>viii) Molar Mass, <stemble-latex content="$M$" /></td>
            <td
              v-for="answer in answers"
              class="py-2 px-2 mx-0 centered-input"
              :key="answer.molarMass"
            >
              <calculation-input
                v-model="inputs[answer.molarMass]"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td><stemble-latex content="$\text{g mol}^{-1}$" /></td>
          </tr>
        </tbody>
      </v-simple-table>
    </p>

    <p class="mb-3">
      c) Using the Word equation editor, write out sample calculations for one trial for your
      determination of the molar mass, as well as the average molar mass and its standard error.
      Then upload a picture of your equation using the field below.
    </p>

    <file-upload-ui-component
      style="width: 100%"
      label="Click here to select a file"
      :max-number-of-files="1"
      :maxFileSize="10"
      class="mb-5"
      accepted-file-extensions=".png, .webp, .jpg ,.jpeg"
      @upload:attachments="sampleCalcs = $event"
    />

    <p class="mb-0">
      d) Calculate your mean of molar masses and the associated standard error. Show all
      calculations. See “<span style="font-style: italic"
        >Method 3: Mean, Standard Deviation, and Standard Error</span
      >” in the document “<span style="font-style: italic">Experimental Uncertainties</span>” on onQ
      for information on how to calculate standard error.
    </p>

    <v-simple-table class="mb-5">
      <thead>
        <tr>
          <td class="pb-n6" style="font-weight: bold; text-align: left">Value</td>
          <td class="pb-n6" style="font-weight: bold; text-align: left; width: 15px" />
          <td class="pb-n6" style="font-weight: bold; text-align: left">Standard Error</td>
          <td class="pb-n6" style="font-weight: bold; text-align: left">Units</td>
        </tr>
        <tr>
          <td style="font-weight: bold; text-align: left">
            <calculation-input
              v-model="inputs.molarMassAvg"
              class="mb-0"
              dense
              :showScientificNotation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td style="font-weight: bold; text-align: left; width: 15px">
            <stemble-latex content="$\pm$" />
          </td>
          <td style="font-weight: bold; text-align: left">
            <calculation-input
              v-model="inputs.molarMassError"
              class="mb-0"
              dense
              :showScientificNotation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td style="font-weight: bold; text-align: left">
            <stemble-latex content="$\text{g mol}^{-1}$" />
          </td>
        </tr>
      </thead>
    </v-simple-table>

    <p class="mb-n3">
      e) Based on the molar mass you determined, which of the following is most likely to be your
      unknown organic compound?
    </p>

    <v-radio-group v-model="inputs.unknownIdentity" :disabled="!allowEditing" class="mb-0 pl-6">
      <v-radio
        v-for="option in options"
        :key="'pt-1-' + option.value"
        class="my-0"
        :value="option.value"
      >
        <template #label>
          <stemble-latex :content="option.text" />
        </template>
      </v-radio>
    </v-radio-group>

    <p class="mb-3">
      f) Provide three sources of experimental error that may have affected your results. Explain
      how these errors would have impacted your results and ultimately your conclusion.
    </p>

    <v-textarea v-model="inputs.errorAnalysis" label="Error Analysis" outlined rows="7" auto-grow />
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import STextarea from '@/common/components/STextarea.vue';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import FileUploadUiComponent from '@/tasks/components/task-ui-components/FileUploadUiComponent.vue';

export default {
  name: 'QueensLab3Results',
  components: {
    FileUploadUiComponent,
    CalculationInput,
    StembleLatex,
    STextarea,
  },
  mixins: [DynamicQuestionMixin()],
  props: {
    isSubmitting: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        sampleNumber: null,

        emptyMassT1: null,
        fullMassT1: null,
        massVapourT1: null,
        volumeT1: null,
        tempT1: null,
        pressureT1: null,
        molarMassT1: null,

        emptyMassT2: null,
        fullMassT2: null,
        massVapourT2: null,
        volumeT2: null,
        tempT2: null,
        pressureT2: null,
        molarMassT2: null,

        emptyMassT3: null,
        fullMassT3: null,
        massVapourT3: null,
        volumeT3: null,
        tempT3: null,
        pressureT3: null,
        molarMassT3: null,

        molarMassAvg: null,
        molarMassError: null,

        unknownIdentity: null,
        errorAnalysis: null,
      },
      answers: [
        {
          emptyMass: 'emptyMassT1',
          fullMass: 'fullMassT1',
          massVapour: 'massVapourT1',
          volume: 'volumeT1',
          temp: 'tempT1',
          pressure: 'pressureT1',
          molarMass: 'molarMassT1',
        },
        {
          emptyMass: 'emptyMassT2',
          fullMass: 'fullMassT2',
          massVapour: 'massVapourT2',
          volume: 'volumeT2',
          temp: 'tempT2',
          pressure: 'pressureT2',
          molarMass: 'molarMassT2',
        },
        {
          emptyMass: 'emptyMassT3',
          fullMass: 'fullMassT3',
          massVapour: 'massVapourT3',
          volume: 'volumeT3',
          temp: 'tempT3',
          pressure: 'pressureT3',
          molarMass: 'molarMassT3',
        },
      ],
      sampleCalcs: [],
      options: [
        {text: 'Acetone', value: 'acetone'},
        {text: 'Ethanol', value: 'ethanol'},
        {text: 'Isopropanol', value: 'isopropanol'},
        {text: 'Methanol', value: 'methanol'},
      ],
    };
  },
  computed: {
    attachments() {
      return [...this.sampleCalcs];
    },
  },
};
</script>

<style></style>
